import React, { SelectHTMLAttributes, forwardRef } from 'react';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    error?: string;
    helperText?: string;
    options: Array<{ value: string; label: string }>;
    placeholder?: string;
    fullWidth?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
    (
        {
            label,
            error,
            helperText,
            options,
            placeholder = 'Select an option',
            fullWidth = false,
            className = '',
            id,
            ...props
        },
        ref
    ) => {
        const selectId = id || `select-${label?.replace(/\s+/g, '-').toLowerCase()}`;

        return (
            <div className={`${fullWidth ? 'w-full' : ''}`}>
                {label && (
                    <label
                        htmlFor={selectId}
                        className="block mb-2 text-sm font-medium text-[#015FA6] font-manrope"
                    >
                        {label}
                    </label>
                )}
                <div className="relative">
                    <select
                        ref={ref}
                        id={selectId}
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
                        className={`
                            w-full h-[55px] px-4 pr-10
                            font-manrope text-base
                            bg-white border rounded-lg
                            transition-all duration-200
                            appearance-none cursor-pointer
                            focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20
                            disabled:bg-gray-100 disabled:cursor-not-allowed
                            ${error ? 'border-red-500 text-gray-700' : props.value ? 'text-gray-700 border-[#CBD5E0]' : 'text-gray-400 border-[#CBD5E0]'}
                            ${error ? 'focus:border-red-500' : 'focus:border-[#015FA6]'}
                            ${className}
                        `}
                        {...props}
                    >
                        <option value="" disabled>
                            {placeholder}
                        </option>
                        {options.map((option) => (
                            <option key={option.value} value={option.value} className="text-gray-700">
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#015FA6"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </div>
                </div>
                {error && (
                    <p
                        id={`${selectId}-error`}
                        role="alert"
                        className="mt-1.5 text-xs text-red-500 font-manrope flex items-center gap-1"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                            <path
                                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                stroke="currentColor"
                                strokeWidth={1.5}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {error}
                    </p>
                )}
                {helperText && !error && (
                    <p id={`${selectId}-helper`} className="mt-1.5 text-xs text-gray-500 font-manrope">
                        {helperText}
                    </p>
                )}
            </div>
        );
    }
);

Select.displayName = 'Select';

export default Select;
